import { ArrowDownTrayIcon } from '@heroicons/react/24/outline';
import { Document, Image, PDFDownloadLink, Page, StyleSheet, Text, View } from '@react-pdf/renderer';
import { Fragment, useEffect, useState } from 'react';
import { filter as _filter } from "underscore";
import str from "lib/str";

const styles = StyleSheet.create({
  page: {
    padding: 40,
    fontSize: 10,
    width: '100%',
    margin: '0 auto',
    backgroundColor: '#ffffff'
  },
  header: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    alignItems: 'center'
  },
  logo: {
    width: 180,
    height: 60,
    objectFit: 'contain'
  },
  title: {
    fontSize: 24,
    fontWeight: 'bold',
    marginBottom: 4
  },
  subtitle: {
    color: '#f59e0b',
    fontWeight: 'medium'
  },
  customerInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 16,
    paddingBottom: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb'
  },
  customerName: {
    fontSize: 12,
    fontWeight: 'bold',
    marginBottom: 4
  },
  customerPhone: {
    color: '#6b7280',
    fontWeight: 'medium'
  },
  table: {
    width: '100%',
    marginBottom: 16,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4,
    borderCollapse: 'collapse',
  },
  tableHeader: {
    flexDirection: 'row',
    backgroundColor: '#f9fafb',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    padding: '8px 12px',
  },
  tableHeaderText: {
    fontWeight: 'bold',
    textAlign: 'right',
    fontSize: 9,
  },
  tableRow: {
    flexDirection: 'row',
    borderBottomWidth: 1,
    borderBottomColor: '#e5e7eb',
    padding: '8px 12px',
    alignItems: 'center',
  },
  tableCell: {
    flex: 1,
    padding: '4px 8px',
    textAlign: 'right',
    fontSize: 9,
  },
  productCell: {
    flex: 2,
    textAlign: 'left',
  },
  orderCell: {
    flex: 2,
    textAlign: 'left',
  },
  qtyCell: {
    flex: 0.5,
    textAlign: 'right',
  },
  priceCell: {
    flex: 1,
    textAlign: 'right',
  },
  totalCell: {
    flex: 1.2,
    textAlign: 'right',
    fontWeight: 'bold',
  },
  productInfo: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: 8
  },
  productImage: {
    width: 40,
    height: 40,
    borderWidth: 1,
    borderColor: '#e5e7eb',
    borderRadius: 4
  },
  productDetails: {
    flexDirection: 'column',
    gap: 2
  },
  productName: {
    fontWeight: 'medium'
  },
  productCode: {
    fontSize: 8,
    color: '#6b7280'
  },
  badge: {
    backgroundColor: '#f3f4f6',
    borderRadius: 4,
    padding: '2px 6px',
    marginRight: 4,
    fontSize: 8,
    color: '#4b5563'
  },
  paymentInfo: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginTop: 16
  },
  paymentDetails: {
    width: '50%'
  },
  paymentSummary: {
    width: '40%'
  },
  paymentRow: {
    flexDirection: 'row',
    justifyContent: 'space-between',
    marginBottom: 4
  },
  paymentLabel: {
    textTransform: 'uppercase',
    color: '#6b7280',
    fontWeight: 'light'
  },
  paymentValue: {
    textAlign: 'right'
  },
  totalLabel: {
    fontWeight: 'bold',
    color: '#374151'
  },
  divider: {
    height: 1,
    backgroundColor: '#e5e7eb',
    marginVertical: 8
  },
  creditText: {
    color: '#2563eb'
  }
});

const formatCurrency = (number) => {
  return new Intl.NumberFormat('id-ID', {
    style: 'currency',
    currency: 'IDR',
    minimumFractionDigits: 0
  }).format(number);
};

const localHelpers = {
  subtotal: (customerId, invoice) => {
    return _filter(invoice?.SalesOrders, {
      customer_id: customerId,
      invoice_id: invoice?.id,
    })
      .map((salesOrder) => {
        return salesOrder?.OrderItems.map(
          (orderDetail) =>
            orderDetail.qty * (orderDetail.unit_price - orderDetail.discount_reseller)
        ).reduce((partialSum, a) => partialSum + a, 0);
      })
      .reduce((partialSum, a) => partialSum + a, 0);
  },
  shipmentCost: (customerId, invoice) => {
    return _filter(invoice?.SalesOrders, {
      customer_id: customerId,
      invoice_id: invoice?.id,
    })
      .map((salesOrder) => {
        return Number(salesOrder.delivery_fee);
      })
      .reduce((partialSum, a) => partialSum + a, 0);
  },
  shipmentCostDiscount: (customerId, invoice) => {
    return _filter(invoice?.SalesOrders, {
      customer_id: customerId,
      invoice_id: invoice?.id,
    })
      .map((salesOrder) => {
        return Number(salesOrder.delivery_fee_discount);
      })
      .reduce((partialSum, a) => partialSum + a, 0);
  },
  total: (customerId, invoice) => {
    return (
      +localHelpers.subtotal(customerId, invoice) +
      +localHelpers.shipmentCost(customerId, invoice)
      // +localHelpers.shipmentCostDiscount(customerId)
    );
  },
  subTotalPerProduct: (orderItems) => {
    return orderItems?.reduce((total, order) => {
      total += Number(order.unit_price - order.discount_reseller) * Number(order.qty);
      return total;
    }, 0);
  },  
  discountTransaction: (customer_id, invoice) => {
    console.log("initial sales", invoice?.SalesOrders);
    return _filter(invoice?.SalesOrders, {
      customer_id: customer_id,
      invoice_id: invoice?.id,
    }).reduce((a, b) => {
      return Number(a) + Number(b.discount_transaction);
    }, 0);
  },
  totalAfterDiscount: (customer_id, invoice) => {
    return (
      +localHelpers.subtotal(customer_id, invoice) +
      localHelpers.shipmentCost(customer_id, invoice) -
      localHelpers.discountTransaction(customer_id, invoice)
    );
  },
  payment: (payments) => {
    var total = 0;
    payments?.map((payment, key) => (total += Number(payment.total_payment)));
    return total;
  },
};

const PDFInvoiceComponent = ({ invoice }) => {
  const [pdfDocument, setPdfDocument] = useState(null);

  useEffect(() => {
    const generatePdf = () => {
      const MyDocument = (
        <Document>
          {invoice?.map((object, index) => (
            <Page style={styles.page} size="A4">
              {/* Header */}
              <View style={styles.header}>
                <View>
                  <Text style={styles.title}>Invoice</Text>
                  <Text style={styles.subtitle}>{object?.inv_code}</Text>
                </View>
                <Image
                  style={styles.logo}
                  src="/client-logo.png"
                />
              </View>

              {/* Customer Info */}
              <View style={styles.customerInfo}>
                <View>
                  <Text style={styles.customerName}>
                    {[object?.customer?.firstName, object?.customer?.middleName, object?.customer?.lastName]
                      .filter(Boolean)
                      .join(' ')}
                  </Text>
                  <Text style={styles.customerPhone}>{object?.customer?.phone}</Text>
                </View>
                <Text>{str.standardDate(new Date().toISOString(), "id")}</Text>
              </View>

              {/* Order Items Table */}
              <View style={styles.table}>
                {/* Table Header */}
                <View style={styles.tableHeader}>
                  <Text style={[styles.tableCell, styles.productCell]}>Order</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Harga</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Diskon</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Qty</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Ongkir</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Subtotal</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Diskon Order</Text>
                  <Text style={[styles.tableCell, styles.tableHeaderText]}>Total</Text>
                </View>

                {/* Order Items */}
                {object?.SalesOrders?.map((order, orderIndex) => (
                  <Fragment key={orderIndex}>
                    <View style={styles.tableRow}>
                      <View style={[styles.tableCell, styles.productCell]}>
                        <View>
                          <Text style={{ fontWeight: 'bold' }}>{order.order_code}</Text>
                          <Text style={{ color: '#6b7280', fontSize: 8, marginTop: 2 }}>
                            Dikirim ke: {order.Address?.desc}
                          </Text>
                          {order.note_order && (
                            <Text style={{ color: '#6b7280', fontSize: 8, marginTop: 2 }}>
                              Catatan: {order.note_order}
                            </Text>
                          )}
                        </View>
                      </View>
                      <Text style={[styles.tableCell, styles.priceCell]}></Text>
                      <Text style={[styles.tableCell, styles.priceCell]}></Text>
                      <Text style={[styles.tableCell, styles.qtyCell]}></Text>
                      <Text style={[styles.tableCell, styles.priceCell]}>
                        {formatCurrency(order.delivery_fee)}
                      </Text>
                      <Text style={[styles.tableCell, styles.priceCell]}>
                        {formatCurrency(localHelpers.subTotalPerProduct(order.OrderItems))}
                      </Text>
                      <Text style={[styles.tableCell, styles.priceCell]}>
                        {formatCurrency(order.discount_transaction)}
                      </Text>
                      <Text style={[styles.tableCell, styles.totalCell]}>
                        {formatCurrency(Number(localHelpers.subTotalPerProduct(order.OrderItems)) + Number(order.delivery_fee) - Number(order.discount_transaction))}
                      </Text>
                    </View>

                    {order.OrderItems.map((item, itemIndex) => (
                      <View key={itemIndex} style={styles.tableRow}>
                        <View style={[styles.tableCell, styles.productCell]}>
                          <View style={styles.productDetails}>
                            <Text style={styles.productName}>{item.master_product.name}</Text>
                            <Text style={styles.productCode}>{item.master_product.code}</Text>
                            <View style={{ flexDirection: 'row', gap: 4, marginTop: 2 }}>
                              <Text style={styles.badge}>{item.master_color.name}</Text>
                              <Text style={styles.badge}>{item.master_size.name}</Text>
                            </View>
                            {item.notes && (
                              <Text style={{ color: '#6b7280', fontSize: 8, marginTop: 2 }}>
                                Catatan: {item.notes}
                              </Text>
                            )}
                          </View>
                        </View>
                        <Text style={[styles.tableCell, styles.priceCell]}>
                          {formatCurrency(item.unit_price)}
                        </Text>
                        <Text style={[styles.tableCell, styles.priceCell]}>
                          {item.discount_reseller ? formatCurrency(item.discount_reseller) : '-'}
                        </Text>
                        <Text style={[styles.tableCell, styles.qtyCell]}>
                          {item.qty}
                        </Text>
                        <Text style={[styles.tableCell, styles.priceCell]}></Text>
                        <Text style={[styles.tableCell, styles.priceCell]}>
                          {formatCurrency(item.qty * (item.unit_price - (item.discount_reseller || 0)))}
                        </Text>
                        <Text style={[styles.tableCell, styles.priceCell]}></Text>
                        <Text style={[styles.tableCell, styles.totalCell]}></Text>
                      </View>
                    ))}
                  </Fragment>
                ))}
              </View>

              {/* Payment Information */}
              <View style={styles.paymentInfo}>
                <View style={styles.paymentDetails}>
                  <Text style={{ fontWeight: 'medium', marginBottom: 4 }}>
                    Keterangan Pembayaran
                  </Text>
                  <Text style={{ fontSize: 9, marginBottom: 8 }}>
                    Untuk melakukan pembayaran, harap transfer ke:
                  </Text>
                  <View style={{ fontSize: 9, lineHeight: 1.5 }}>
                    <Text>Bank: {process.env.REACT_APP_REK_BANK}</Text>
                    <Text>Nama: {process.env.REACT_APP_REK_NAME}</Text>
                    <Text>Rekening: {process.env.REACT_APP_REK_NO}</Text>
                  </View>
                </View>

                <View style={styles.paymentSummary}>
                  <View style={styles.paymentRow}>
                    <Text style={styles.paymentLabel}>Subtotal:</Text>
                    <Text style={styles.paymentValue}>
                      {formatCurrency(localHelpers?.subtotal(object?.customer_id, object))}
                    </Text>
                  </View>

                  <View style={styles.paymentRow}>
                    <Text style={styles.paymentLabel}>Ongkir:</Text>
                    <Text style={styles.paymentValue}>
                      {formatCurrency(localHelpers?.shipmentCost(object?.customer_id, object))}
                    </Text>
                  </View>
                  <View style={[styles.paymentRow]}>
                    <Text style={[styles.paymentLabel, styles.totalLabel]}>Total:</Text>
                    <Text style={[styles.paymentValue, styles.totalLabel]}>
                      {formatCurrency(localHelpers?.total(object?.customer_id, object))}
                    </Text>
                  </View>
                  <View style={styles.paymentRow}>
                    <Text style={[styles.paymentLabel, styles.totalLabel]}>Diskon Transaksi:</Text>
                    <Text style={[styles.paymentValue, styles.totalLabel]}>
                      {formatCurrency(localHelpers?.discountTransaction(object?.customer_id, object))}
                    </Text>
                  </View>

                  <View style={styles.divider}></View>

                  {object?.total_payment >= 0 && (
                    <View style={styles.paymentRow}>
                      <Text style={[styles.paymentLabel, styles.creditText]}>
                        Pembayaran:
                      </Text>
                      <Text style={[styles.paymentValue, styles.creditText]}>
                        {formatCurrency(object?.total_payment)}
                      </Text>
                    </View>
                  )}

                  <View style={styles.paymentRow}>
                    <Text style={[styles.paymentLabel, { fontWeight: 'bold' }]}>
                      Sisa Tagihan:
                    </Text>
                    <Text style={[styles.paymentValue, { fontWeight: 'bold' }]}>
                      {formatCurrency(
                        localHelpers?.totalAfterDiscount(object?.customer_id, object) - 
                        object?.total_payment
                      )}
                    </Text>
                  </View>
                </View>
              </View>
            </Page>
          ))}
        </Document>
      );
      setPdfDocument(MyDocument);
    };

    generatePdf();
  }, [invoice]);

  return (
    <div className="flex justify-end md:justify-start">
      {invoice && invoice.length > 0 ? (pdfDocument ? (
        <PDFDownloadLink
          document={pdfDocument}
          fileName={`Invoice_${invoice?.inv_code || 'new'}.pdf`}
          className="flex items-center gap-2 text-sm text-gray-600 hover:text-gray-900"
        >
          {({ blob, url, loading, error }) => (
            <>
              <ArrowDownTrayIcon className="h-6 w-6" />
              {loading ? 'Membuat PDF...' : 'Unduh Invoice'}
            </>
          )}
        </PDFDownloadLink>
      ) : (
        <div>Menyiapkan PDF...</div>
      )) : (
        <button className="flex items-center gap-2 text-sm text-gray-400 cursor-not-allowed" disabled title="Pilih setidaknya satu item untuk diunduh"><ArrowDownTrayIcon className="h-6 w-6" /> Unduh Invoice</button>
      )}
    </div>
  );
};

export default PDFInvoiceComponent;